import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Table, Button, Badge, Collapse, ListGroup, ListGroupItem, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

// Dummy SBOM data with resolved state added


// Component to render SBOM report
const SbomReport = ({sbomData}) => {
  
  const { scanId, timestamp, Basic, analysis } = sbomData;
  const { summary } = analysis || {};
  const { total_vulnerabilities = "0", risk_score = "0", actions_needed = [] } = summary || {};

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [collapseOpen, setCollapseOpen] = useState(true);
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username; 
  const [resolvedState, setResolvedState] = useState(() =>
    analysis.components.reduce((acc, component) => {
      acc[component.name] = component.resolved; // Initialize from JSON data
      return acc;
    }, {})
  );


  const toggleActionModal = (action) => {
    setSelectedAction(action);
    setActionModalOpen(!actionModalOpen);
  };

  const renderRiskScore = (score) => {
    if (score >= 8) {
      return <strong className="text-danger">{score}</strong>; // Bootstrap danger class (red)
    }
    if (score >= 5) {
      return <strong className="text-warning">{score}</strong>; // Bootstrap warning class (orange)
    }
    return <strong className="text-success">{score}</strong>; // Bootstrap success class (green)
  };

  // Handle resolved state for each component
  const toggleResolved = async (componentName) => {
    const newState = !resolvedState[componentName];
  
    try {
      // Perform the PATCH request to update the resolved state on the backend
      const response = await fetch(`https://api.bluhawk.live/api/vulnerability-scan/${userId}/results/${scanId}/components/${componentName}`, {
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resolved: newState }),
      });
  
      if (!response.ok) {
        throw new Error("Failed to update resolved state on the server.");
      }
  
      // If the request is successful, update the local state and sbomData
      setResolvedState((prevState) => {
        const updatedComponents = analysis.components.map((component) => {
          if (component.name === componentName) {
            component.resolved = newState; // Update the resolved state in JSON
          }
          return component;
        });
        // Update the sbomData
        sbomData.analysis.components = updatedComponents;
        return { ...prevState, [componentName]: newState };
      });
  
      console.log(`Successfully updated resolved state for ${componentName}`);
    } catch (error) {
      console.error("Error updating resolved state:", error);
    }
  };
  

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card className="shadow-sm rounded-lg">
              <CardBody>
                <h4 className="card-title text-primary">SBOM Report - {scanId}</h4>
                <p className="text-muted">Scan Date: {timestamp}</p>

                {/* Scan Summary */}
                <div className="d-flex flex-column mb-4">
                  <h5 className="font-weight-bold">Scan Summary</h5>
                  <div className="d-flex justify-content-between">
                    <p><strong>Total Vulnerabilities:</strong> {total_vulnerabilities}</p>
                    <p><strong>Risk Score:</strong> <span style={{ fontSize: "1.2em" }}>{renderRiskScore(risk_score)}</span></p>
                  </div>
                  <h6>Actions Needed:</h6>
                  <ListGroup>
                    {actions_needed.map((action, index) => (
                      <ListGroupItem
                        key={index}
                        className="list-group-item-action"
                        onClick={() => toggleActionModal(action)}
                        style={{ cursor: "pointer", display: "flex", alignItems: "center", verticalAlign: "middle" }}
                      >
                        <i className="bx bx-warning-alt mr-2" style={{ fontSize: "1.5em" }} />
                        <span>{action}</span>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </div>

                {/* Vulnerable Components */}
                <div className="mb-4">
                  <h5 className="font-weight-bold">Vulnerable Components</h5>
                  <Table bordered responsive hover>
                    <thead className="bg-light">
                      <tr>
                        <th style={{ verticalAlign: "middle" }}>Component Name</th>
                        <th style={{ verticalAlign: "middle" }}>Recommendation</th>
                        <th style={{ verticalAlign: "middle" }}>Additional Considerations</th>
                        <th style={{ verticalAlign: "middle" }}>Status</th>
                        <th style={{ verticalAlign: "middle" }}>Risk Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.components.map((component, index) => (
                        <tr key={index}>
                          <td style={{ verticalAlign: "middle" }}>{component.name}</td>
                          <td style={{ verticalAlign: "middle" }}>{component.recommendation}</td>
                          <td style={{ verticalAlign: "middle" }}>
                            <Collapse isOpen={collapseOpen}>
                              <p>{component.additional_considerations}</p>
                            </Collapse>
                          </td>
                          <td style={{ fontSize: "1.2em", verticalAlign: "middle" }}>
                            {component.status === "Vulnerable" ? (
                              <Badge color="danger" style={{ width: '160px', textAlign: 'center', padding: '5px', display: 'inline-block' }}>
                                <i className="bx bx-error-circle mr-2" />
                                {component.status}
                              </Badge>
                            ) : component.status === "Potentially Vulnerable" ? (
                              <Badge color="warning" style={{ width: '160px', textAlign: 'center', padding: '5px', display: 'inline-block' }}>
                                <i className="bx bx-error-circle mr-2" />
                                {component.status}
                              </Badge>
                            ) : (
                              <Badge color="success" style={{ width: '160px', textAlign: 'center', padding: '5px', display: 'inline-block' }}>
                                <i className="bx bx-check-circle mr-2" />
                                {component.status}
                              </Badge>
                            )}
                          </td>
                          <td style={{ fontSize: "1.2em", verticalAlign: "middle" }}>{renderRiskScore(component.component_risk_score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Additional Considerations */}
                <div className="mb-4">
                  <h5 className="font-weight-bold" style={{ marginBottom: "1rem" }}>Additional Considerations</h5>
                  <div>
                    {analysis.components.map((component, index) => (
                      <div key={index} className="d-flex justify-content-between mb-3">
                        <p>{component.name}: {component.additional_considerations}</p>
                        <Button
                          color={resolvedState[component.name] ? "success" : "danger"}
                          size="sm"
                          style={{ width: '120px', padding: '6px 6px' }}
                          onClick={() => toggleResolved(component.name)}
                        >
                          {resolvedState[component.name] ? "Resolved ✔" : "Not Resolved"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Export Button */}
                <Button color="primary" className="px-4 py-2">
                  <i className="bx bx-download mr-2" />
                  Export Results
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal for Actions Needed */}
      <Modal isOpen={actionModalOpen} toggle={() => toggleActionModal(selectedAction)} centered>
        <ModalHeader toggle={() => toggleActionModal(selectedAction)}>Action Details</ModalHeader>
        <ModalBody>
          <p>{selectedAction}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => toggleActionModal(selectedAction)}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SbomReport;
